import { kea } from 'kea'
import PropTypes from "prop-types";
import {get, forEach, pick, values, compact} from "lodash";
import {call, put} from "redux-saga/effects";
import { Contacts, Carts } from '@gratify-eu/entities'

import {
  NAME_LAST_NAME,
  NAME_FULL_NAME,
  ADDRESS_FOR_COMPANY_ONLY,
  ADDRESS_ENABLED,
  ADDRESS_LINE_2_HIDDEN,
  PHONE_NUMBER_OPTIONAL,
  PHONE_NUMBER_REQUIRED
} from '~/constants/checkout-settings'

import { __ } from '~/i18n'

import entities from '~/entities'
import createForm from '~/utils/create-form'

import { propTypes, contact, defaults, fillWithDefaults } from '~/components/contacts'
export const form = createForm({propTypes, defaults});

const validateAddress = (address) => {
  if(!address) {
    return false;
  }
  const requiredValues = pick(address, ['address', 'city', 'postal_code', 'state', 'country']);
  const valuesData = values(requiredValues);

  // if one of the required values is missing we should show fields.
  return valuesData.length === compact(valuesData).length;
};

export default kea({
  path: () => ['scenes', 'shipping', 'form'],
  connect: {
    actions: [
      form, [
        'setValue',
        'setValues',
        'submit',
        'submitSuccess',
        'submitFailure'
      ]
    ],
    values: [
      form, [
        'values',
        'isSubmitting',
        'errors',
        'submitErrors'
      ]
    ]
  },
  actions: () => ({
    setFormLoading: true,
    formLoaded: true
  }),

  reducers: ({actions, constants}) => ({
    formLoading: [false, PropTypes.bool, {
      [actions.setFormLoading]: () => (true),
      [actions.formLoaded]: () => (false),
    }],
  }),

  selectors : ({ constants, selectors }) => ({
    shippingMethod: [
      () => [
        (state, props) => (props && props.shippingMethod)
      ], (shippingMethod) => {
        return shippingMethod;
      },
      PropTypes.object
    ],
    shippingMethodOptions: [
      () => [
        (state, props) => (props && props.shippingMethodOptions),
      ], (shippingMethodOptions) => {
        return shippingMethodOptions
      },
      PropTypes.object
    ],
    useBillingAddress: [
      () => [
        (state, props) => (props && props.useBillingAddress),
      ],
      (useBillingAddress) => {
        return useBillingAddress
      },
      PropTypes.bool
    ],
    billingContact: [
      () => [
        (state, props) => (props && props.billingContact),
      ],
      (billingContact) => {
        return billingContact
      },
      PropTypes.object
    ],
    shippingContact: [
      () => [
        (state, props) => (props && props.shippingContact),
      ],
      (shippingContact) => {
        return shippingContact
      },
      PropTypes.object
    ],
    contactFields: [
      () => [
        selectors.shippingMethod,
        selectors.useBillingAddress,
        selectors.billingContact,
      ], (shippingMethod, useBillingAddress, billingContact) => {

        const fields = contact;

        if(shippingMethod) {
          const {
            requires_name, requires_email, requires_phone, requires_address
          } = shippingMethod;

          let chosenFields = {
          };

          if(requires_name) {
            if(!(useBillingAddress && get(billingContact, 'first_name'))) {
              chosenFields.first_name = fields.first_name;
            }
            if(!(useBillingAddress && get(billingContact, 'last_name'))) {
              chosenFields.last_name = fields.last_name;
            }
          }

          if(requires_email) {
            if(!(useBillingAddress && get(billingContact, 'email'))) {
              chosenFields.email = fields.email;
            }
          }

          if(requires_phone) {
            if(!(useBillingAddress && get(billingContact, 'phone'))) {
              chosenFields.phone = fields.phone;
            }
          }

          if(requires_address) {
            if(!(useBillingAddress && get(billingContact, 'company'))) {
              chosenFields.company = fields.company;
            }

            if(!(useBillingAddress && validateAddress(get(billingContact, 'address')))) {
              chosenFields.address = Object.assign({}, fields.address);
            }
          }
          return chosenFields;
        }
        return fields;
      },
      PropTypes.object
    ]
  }),
  start: function* () {

  },
  takeLatest: ({ actions, workers }) => ({
    [actions.submit]: function * () {
      const { submitSuccess, submitFailure } = this.actionCreators
      const { setShippingMethodErrors } = this.props;
      try {
        const values = yield this.get('values');
        const activeShop = yield entities.get('activeShop');
        const contactFields = yield this.get('contactFields');
        const shippingContact = yield this.get('shippingContact');
        const useBillingAddress = yield this.get('useBillingAddress');
        const billingContact = yield this.get('billingContact');
        const shippingMethod = yield this.get('shippingMethod');
        const shippingMethodOptions = yield this.get('shippingMethodOptions');

        let contactData = {}
        if(useBillingAddress) {
          contactData = {...defaults, ...billingContact, shop: activeShop.url};
        } else if(shippingContact && shippingContact.id) {
          contactData = {...defaults, ...shippingContact, shop: activeShop.url};
        } else {
          contactData = {...defaults, shop: activeShop.url};
        }

        forEach(contactFields, (field, key) => {
          contactData[key] = values[key];
        });

        if(!contactFields.address && !useBillingAddress) {
          contactData.address = null;
        }

        let contact;
        if(contactData && contactData.id) {
          contact = yield call(Contacts.workers.update, Contacts.actions.update.request(contactData.id, contactData), true);
        }else {
          contact = yield call(Contacts.workers.create, Contacts.actions.create.request(contactData), true);
        }


        const activeCart = yield entities.get('activeCart');

        try {
          if(!(shippingMethod && shippingMethod.url)) {
            yield put(setShippingMethodErrors([__("Please choose a shipping method")]));
            yield put(submitFailure());
            return;
          }
          yield call(Carts.workers.update, Carts.actions.update.request(activeCart.id, {
            shipping_contact: contact.url,
            shipping_method: shippingMethod.url,
            shipping_method_options: shippingMethodOptions || {},
          }), true);

          yield put(submitSuccess(contact))

        } catch(error) {
          console.log('error', error);
          const shippingMethodErrors = get(error, 'response.data.shipping_method', []);
          if(shippingMethodErrors && shippingMethodErrors.length > 0) {
            yield put(submitFailure());
            yield put(setShippingMethodErrors(shippingMethodErrors));
          } else {
            const contactErrors = get(error, 'response.data.shipping_contact', {});
            yield put(submitFailure(contactErrors))
          }
        }

      } catch (error) {
        const errors = get(error, 'response.data');
        if(errors) {
          yield put(submitFailure(errors))
        }
        console.log('error', error);
      }
    }
  })
})
